import React from 'react'
import type { Block, Variable } from '../../types'
import { PropertyLabel, PropertyInput, ToggleSwitch } from './shared'

interface TogglePropertiesProps {
  block: Block
  variable: Variable
  linkedBlocks: Block[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
  onUpdateVariable: (name: string, updates: Partial<Variable>) => void
  onRenameVariable: (oldName: string, newName: string) => void
}

export default function ToggleProperties({
  block,
  variable,
  linkedBlocks,
  onUpdateBlock,
  onUpdateVariable,
  onRenameVariable,
}: TogglePropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>

  const variant = (config.variant as string) || 'switch'

  return (
    <>
      <div className="space-y-2">
        <PropertyLabel>Style</PropertyLabel>
        <div className="grid grid-cols-2 gap-1.5">
          {([
            { value: 'switch', label: 'Switch' },
            { value: 'pill', label: 'Pill' },
            { value: 'checkbox', label: 'Checkbox' },
            { value: 'segmented', label: 'Segmented' },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              onClick={() => onUpdateBlock(block.id, { config: { ...config, variant: opt.value } })}
              className={`py-2 px-2 rounded-lg border text-xs font-medium transition-all ${
                variant === opt.value
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Label</PropertyLabel>
        <PropertyInput
          value={block.label || ''}
          onChange={(e) => onUpdateBlock(block.id, { label: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <PropertyLabel>Linked Variable</PropertyLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[#5048e5] font-bold">@</span>
          <input
            type="text"
            value={variable.name}
            onChange={(e) =>
              onRenameVariable(variable.name, e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())
            }
            className="w-full font-mono bg-slate-50 border border-slate-200 rounded-lg p-2.5 pl-7 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
          />
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Default Value</PropertyLabel>
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">
            {variable.default ? (config.onLabel as string) || 'ON' : (config.offLabel as string) || 'OFF'}
          </span>
          <ToggleSwitch
            checked={Boolean(variable.default)}
            onChange={(v) => onUpdateVariable(variable.name, { default: v })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <PropertyLabel>Off Label</PropertyLabel>
          <PropertyInput
            value={(config.offLabel as string) || 'OFF'}
            onChange={(e) => onUpdateBlock(block.id, { config: { ...config, offLabel: e.target.value } })}
          />
        </div>
        <div className="space-y-2">
          <PropertyLabel>On Label</PropertyLabel>
          <PropertyInput
            value={(config.onLabel as string) || 'ON'}
            onChange={(e) => onUpdateBlock(block.id, { config: { ...config, onLabel: e.target.value } })}
          />
        </div>
      </div>

      {linkedBlocks.length > 0 && (
        <div className="pt-4 border-t border-slate-100">
          <PropertyLabel>Linked Blocks</PropertyLabel>
          <ul className="space-y-1 mt-2">
            {linkedBlocks.map((lb) => (
              <li key={lb.id} className="text-sm text-slate-500">
                &bull; {lb.content?.slice(0, 30) || lb.label || lb.type}{' '}
                <span className="text-xs">({lb.type})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}
